import { CustomTable } from '@/components';
import { LINK, PATH } from '@/constants';
import { ShipmentPushStatus, ShipmentStatus } from '@/constants/shipment';
import { capitalizeFirstLetter, convertCurrency, handleOpenChat, spliceContent } from '@/helpers';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import { CopyIcon } from '@/icons';
import { useLazyGetShipmentDetailQuery, useLazyGetUnfulfilledShipmentDetailQuery } from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import commonSlice from '@/redux/features/common.slice';
import shipmentSlice, {
  courierModalSelector,
  shipmentFilterSelector,
  statusModalSelector,
} from '@/redux/features/shipment.slice';
import { IResponse } from '@/types';
import { IShipmentSearch, IShipmentSearchParams, ITrackingDetail } from '@/types/shipment';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  EmptySearchResult,
  Icon,
  IndexTable,
  InlineStack,
  Link,
  Text,
  Tooltip,
  useIndexResourceState,
} from '@shopify/polaris';
import { ChatReferralIcon, EditIcon, ExternalSmallIcon, InfoIcon } from '@shopify/polaris-icons';
import { IndexTableHeading } from '@shopify/polaris/build/ts/src/components/IndexTable';
import { NonEmptyArray } from '@shopify/polaris/build/ts/src/types';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EShipmentType } from '../../config';
import CourierModal from './components/CourierModal';
import ShipmentFilter from './components/ShipmentFilter';
import StatusModal from './components/StatusModal';
import { getShipmentStatusInfo, NOT_DETECTED_TRACKING_COMPANIES } from './config';
import useTrackingPageUrl from '@/hooks/useTrackingPageUrl';

// Fulfillment status configuration for unfulfilled shipments
const FULFILLMENT_STATUS_CONFIG: Record<
  string,
  { label: string; tone: 'warning' | 'info' | 'attention' | 'success' | 'critical' }
> = {
  unfulfilled: { label: 'Unfulfilled', tone: 'warning' },
  in_progress: { label: 'In Progress', tone: 'info' },
  on_hold: { label: 'On Hold', tone: 'attention' },
  restocked: { label: 'Restocked', tone: 'critical' },
  partial: { label: 'Partial', tone: 'info' },
};

const getFulfillmentStatusBadge = (status?: string) => {
  if (!status) return <Badge tone="warning">Unfulfilled</Badge>;
  const config = FULFILLMENT_STATUS_CONFIG[status.toLowerCase()];
  if (config) {
    return <Badge tone={config.tone}>{config.label}</Badge>;
  }
  return <Badge>{capitalizeFirstLetter(status.split('_').join(' '))}</Badge>;
};

interface ShipmentTableProps {
  data: IResponse<IShipmentSearch> | undefined;
  isLoading: boolean;
  isFetching: boolean;
  unRequiredParams: IShipmentSearchParams;
  courierRange?: { from?: string; to?: string };
}

const ShipmentTable = ({ data, isLoading, isFetching, unRequiredParams, courierRange }: ShipmentTableProps) => {
  const navigate = useConfirmationNavigate();
  const dispatch = useDispatch();
  const { shop } = useSelector(tokenSelector);
  const { proxyPath } = useTrackingPageUrl();
  const shipmentFilter = useSelector(shipmentFilterSelector);
  const courierModal = useSelector(courierModalSelector);
  const statusModal = useSelector(statusModalSelector);
  const shipmentList = (data?.data?.shipments || []).map((shipment) => ({
    ...shipment,
    id: shipment.id ? shipment.id.toString() : shipment.orderId,
  }));
  const selectableShipments = shipmentList.filter((shipment) => shipment.pushStatus !== ShipmentPushStatus.OVER_QUOTA);
  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
    useIndexResourceState(selectableShipments);

  const handleSelectCourierByCheckbox = () => {
    const selectedIds = selectedResources.map((item) => Number(item));
    const processingShipments = shipmentList.filter(
      (shipment) => selectedIds.includes(Number(shipment.id)) && shipment.pushStatus === ShipmentPushStatus.PROCESSING,
    );
    if (processingShipments.length > 0) {
      dispatch(
        commonSlice.actions.handleToast({
          content: `${processingShipments.length} shipment${
            processingShipments.length > 1 ? 's are' : ' is'
          } being processed and will be skipped.`,
          isOpen: true,
          error: true,
        }),
      );
    }
    const editableIds = selectedIds.filter((id) => {
      const s = shipmentList.find((sh) => Number(sh.id) === id);
      return s && s.pushStatus !== ShipmentPushStatus.PROCESSING;
    });
    if (editableIds.length === 0) return;
    dispatch(shipmentSlice.actions.handleChangeCourierModal({ ...courierModal, open: true, id: editableIds }));
  };

  const handleClickPaginationBtn = (type: 'next' | 'prev') =>
    dispatch(
      shipmentSlice.actions.handleChangeTableFilter({
        ...shipmentFilter,
        page: type === 'prev' ? shipmentFilter.page - 1 : shipmentFilter.page + 1,
      }),
    );

  const [triggerGetShipmentQuery] = useLazyGetShipmentDetailQuery();
  const [triggerGetUnfulfilledShipmentQuery] = useLazyGetUnfulfilledShipmentDetailQuery();

  const handleNavigate = async (id: string) => {
    try {
      if (shipmentFilter.type === EShipmentType.UNFULFILLED_SHIPMENT) {
        await triggerGetUnfulfilledShipmentQuery({ id: Number(id) }).unwrap();
      } else {
        await triggerGetShipmentQuery({ id: Number(id) }).unwrap();
      }
      // Include type in URL query params to persist on page reload
      const typeParam = shipmentFilter.type ? `?type=${shipmentFilter.type}` : '';
      navigate(`${PATH.SHIPMENT}/${id.toString()}${typeParam}`, {
        state: { filter: unRequiredParams },
      });
    } catch (err) {
      dispatch(
        commonSlice.actions.handleToast({
          content: (err as any)?.data?.message || 'Failed to fetch shipment details',
          isOpen: true,
          error: true,
        }),
      );
      console.error('Error:', err);
    }
  };

  const rowMarkup = shipmentList.map((shipment, index) => {
    const shippingAddressData = shipment.shippingAddress || {};
    const { address1, address2, city, zip, province, country } = shippingAddressData;
    const shippingAddress = [address1, address2, city, zip, province, country].filter((addr) => addr).join(', ');

    const info = getShipmentStatusInfo(
      Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA
        ? ShipmentStatus.OverQuota
        : Number(shipment.pushStatus) === ShipmentPushStatus.NOT_DETECT
        ? ShipmentStatus.CourierNotDetected
        : Number(shipment.pushStatus) === ShipmentPushStatus.OUT_RANGE_QUOTA
        ? ShipmentStatus.OutOfRange
        : (shipment.trackingSummary as ShipmentStatus),
    );
    const renderSyncStatus = () => {
      if (shipment.trackingSummary === ShipmentStatus.CourierNotSupported) {
        return <Badge>none</Badge>;
      }
      if (shipment.pushStatus) {
        return <Badge tone="success">Synced</Badge>;
      }
      return <Badge tone="attention">Syncing</Badge>;
    };

    const handleClickEditCourierBtn = (id: number, courier: string) =>
      dispatch(shipmentSlice.actions.handleChangeCourierModal({ ...courierModal, open: true, value: courier, id: [id] }));

    const handleClickEditStatusBtn = (orderId: string, trackingNumber: string, trackingDetail: ITrackingDetail[]) =>
      dispatch(
        shipmentSlice.actions.handleChangeStatusModal({
          ...statusModal,
          open: true,
          orderId,
          trackingNumber,
          history: trackingDetail,
        }),
      );

    if (shipmentFilter.type === EShipmentType.TRACKED_SHIPMENT) {
      return (
        <IndexTable.Row
          disabled={Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA}
          id={shipment.id.toString()}
          key={shipment.id}
          position={index}
          selected={selectedResources.includes(shipment.id.toString())}
          onClick={() => handleNavigate(shipment.id)}
        >
          <IndexTable.Cell>
            <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <Text variant="headingSm" as="h6">
                {shipment.trackingNumber}
              </Text>
              <CopyIcon copiedText={shipment.trackingNumber} tone="subdued" />
              <div style={{ marginTop: 4 }}>
                <Tooltip content="Go to tracking page">
                  <Button
                    disabled={Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA}
                    icon={ExternalSmallIcon}
                    variant="plain"
                    external
                    url={LINK.TRACKING_ORDER_NOW(shop, shipment.trackingNumber, proxyPath)}
                  />
                </Tooltip>
              </div>
            </div>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <InlineStack blockAlign="center" gap="050">
                <Text variant="headingSm" as="h6">
                  {shipment.name}
                </Text>
                <CopyIcon copiedText={shipment.name} tone="subdued" />
                <div style={{ marginTop: 4 }}>
                  <Tooltip content="Go to order page">
                    <Button
                      disabled={Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA}
                      icon={ExternalSmallIcon}
                      variant="plain"
                      external
                      url={LINK.ORDER_DETAIL(shop, String(shipment.orderId))}
                    />
                  </Tooltip>
                </div>
              </InlineStack>
            </div>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <div
              onClick={(e) => {
                if (shipment.trackingSummary === ShipmentStatus.CourierNotSupported) {
                  e.stopPropagation();
                  if (Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA) return;
                  handleClickEditStatusBtn(shipment.orderId, shipment.trackingNumber, shipment.trackingDetail);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {shipment.trackingSummary ? (
                <Badge progress={info?.progress} tone={info?.tone}>
                  {Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA
                    ? 'Over quota'
                    : Number(shipment.pushStatus) === ShipmentPushStatus.NOT_DETECT
                    ? 'Courier not detected'
                    : Number(shipment.pushStatus) === ShipmentPushStatus.OUT_RANGE_QUOTA
                    ? 'Out of range'
                    : shipment.trackingSummary === ShipmentStatus.NotFound
                    ? 'No info'
                    : capitalizeFirstLetter(shipment.trackingSummary.split('_').join(' '))}
                </Badge>
              ) : (
                '---'
              )}

              {shipment.trackingSummary === ShipmentStatus.CourierNotSupported &&
                Number(shipment.pushStatus) !== ShipmentPushStatus.OVER_QUOTA && (
                  <Button variant="plain" icon={EditIcon}>
                    Edit
                  </Button>
                )}
              {shipment.trackingSummary === ShipmentStatus.NotFound &&
                Number(shipment.pushStatus) !== ShipmentPushStatus.OVER_QUOTA && (
                  <Tooltip
                    content="Carrier may not have updated tracking yet. Tracking information will appear once available."
                    dismissOnMouseOut
                  >
                    <Icon source={InfoIcon} tone="subdued" />
                  </Tooltip>
                )}
              {shipment.trackingSummary === ShipmentStatus.Pending &&
                Number(shipment.pushStatus) !== ShipmentPushStatus.OVER_QUOTA && (
                  <Tooltip
                    content="Tracking information may take some time to appear. Please check back later."
                    dismissOnMouseOut
                  >
                    <Icon source={InfoIcon} tone="subdued" />
                  </Tooltip>
                )}
            </div>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <InlineStack blockAlign="center" gap="100">
              {shipment.trackingCompany}
              {shipment.trackingNumber && (
                <>
                  {(() => {
                    const isPendingAfterPush =
                      shipment.trackingSummary === ShipmentStatus.Pending && shipment.pushStatus === ShipmentPushStatus.COMPLETED;
                    const shouldChangeCarrier =
                      shipment.pushStatus !== ShipmentPushStatus.OVER_QUOTA &&
                      (shipment.trackingSummary === ShipmentStatus.CourierNotSupported ||
                        shipment.trackingSummary === ShipmentStatus.NotFound ||
                        shipment.pushStatus === ShipmentPushStatus.NOT_DETECT ||
                        isPendingAfterPush ||
                        NOT_DETECTED_TRACKING_COMPANIES.some(
                          (company) =>
                            shipment.trackingCompany && company.toLowerCase() === shipment.trackingCompany.toLowerCase(),
                        ));
                    return shouldChangeCarrier;
                  })() && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <ButtonGroup gap="extraTight">
                        <Tooltip content="Change carrier">
                          <Button
                            icon={EditIcon}
                            variant="plain"
                            onClick={() => handleClickEditCourierBtn(Number(shipment.id), shipment.trackingCompany)}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                        {shipment.trackingSummary === ShipmentStatus.CourierNotSupported && (
                          <Tooltip content="Chat support for carrier integration">
                            <Button
                              disabled={Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA}
                              icon={ChatReferralIcon}
                              variant="plain"
                              onClick={handleOpenChat}
                            />
                          </Tooltip>
                        )}
                      </ButtonGroup>
                    </div>
                  )}
                </>
              )}
            </InlineStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            {spliceContent(shipment.trackingDetail?.[0]?.message || '---', 30)}
            {shipment.trackingDetail?.[0]?.message && (
              <span>{dayjs(shipment.trackingDetail?.[0]?.date_time).format('MMM DD, YYYY hh:mm A')}</span>
            )}
          </IndexTable.Cell>
          <IndexTable.Cell>{renderSyncStatus()}</IndexTable.Cell>
          <IndexTable.Cell>
            {shipment.fulfillmentUpdatedAt ? dayjs(shipment.fulfillmentUpdatedAt).format('MMM DD, YYYY hh:mm A') : '---'}
          </IndexTable.Cell>
          <IndexTable.Cell>{shipment.processingTime != undefined ? `${shipment.processingTime} days` : '---'}</IndexTable.Cell>
          <IndexTable.Cell>
            {shipment.dispatchLeadTime != undefined ? `${shipment.dispatchLeadTime} days` : '---'}
          </IndexTable.Cell>
          <IndexTable.Cell>{shipment.deliveryTime != undefined ? `${shipment.deliveryTime} days` : '---'}</IndexTable.Cell>{' '}
        </IndexTable.Row>
      );
    }

    if (shipmentFilter.type === EShipmentType.UNFULFILLED_SHIPMENT) {
      // Handle different field names for unfulfilled shipments
      const orderCreatedAt = (shipment as any).orderCreatedAt || shipment.createdAt;
      const fulfillmentStatus = (shipment as any).status || shipment.fulfillmentStatus;
      const itemCount = (shipment as any).items != undefined ? (shipment as any).items : shipment.lineItems?.length || 0;
      const waitingTime = (shipment as any).waitingTime;
      const waitingDays = waitingTime != undefined ? Math.floor(waitingTime) : dayjs().diff(orderCreatedAt, 'day');

      // Extract order number from orderId GID format or use name if available
      const orderName = shipment.name || shipment.orderId?.split('/').pop() || '';

      return (
        <IndexTable.Row
          id={shipment.id.toString()}
          key={shipment.id}
          position={index}
          selected={selectedResources.includes(shipment.id.toString())}
          onClick={() => handleNavigate(shipment.id)}
        >
          <IndexTable.Cell>
            <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <InlineStack blockAlign="center" gap="050">
                <Text variant="headingSm" as="h6">
                  {orderName}
                </Text>
                {orderName && <CopyIcon copiedText={orderName} tone="subdued" />}
                <div style={{ marginTop: 4 }}>
                  <Tooltip content="Go to order page">
                    <Button
                      icon={ExternalSmallIcon}
                      variant="plain"
                      external
                      url={LINK.ORDER_DETAIL(shop, String(shipment.orderId))}
                    />
                  </Tooltip>
                </div>
              </InlineStack>
            </div>
          </IndexTable.Cell>
          <IndexTable.Cell>{dayjs(orderCreatedAt).format('MMM DD, YYYY hh:mm A')}</IndexTable.Cell>
          <IndexTable.Cell>
            {waitingDays} {waitingDays === 1 ? 'day' : 'days'}
          </IndexTable.Cell>
          <IndexTable.Cell>{getFulfillmentStatusBadge(fulfillmentStatus)}</IndexTable.Cell>
          <IndexTable.Cell>
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </IndexTable.Cell>
          <IndexTable.Cell>
            {shipment.subtotalPrice ? convertCurrency(shipment.subtotalPrice, shipment.currency || '') : '---'}
          </IndexTable.Cell>
          <IndexTable.Cell>{shippingAddress || '---'}</IndexTable.Cell>
        </IndexTable.Row>
      );
    }

    return (
      <IndexTable.Row
        disabled={Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA}
        id={shipment.id.toString()}
        key={shipment.id}
        position={index}
        selected={selectedResources.includes(shipment.id.toString())}
        onClick={() => handleNavigate(shipment.id)}
      >
        <IndexTable.Cell>
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <InlineStack blockAlign="center" gap="050">
              <Text variant="headingSm" as="h6">
                {shipment.name}
              </Text>
              <CopyIcon copiedText={shipment.name} tone="subdued" />
              <div style={{ marginTop: 4 }}>
                <Tooltip content="Go to order page">
                  <Button
                    disabled={Number(shipment.pushStatus) === ShipmentPushStatus.OVER_QUOTA}
                    icon={ExternalSmallIcon}
                    variant="plain"
                    external
                    url={LINK.ORDER_DETAIL(shop, String(shipment.orderId))}
                  />
                </Tooltip>
              </div>
            </InlineStack>
          </div>
        </IndexTable.Cell>
        <IndexTable.Cell>{dayjs(shipment.lastUpdatedAt).format('MMM DD, YYYY hh:mm A')}</IndexTable.Cell>
        <IndexTable.Cell>{dayjs().diff(shipment.createdAt, 'day')}</IndexTable.Cell>
        <IndexTable.Cell>
          <div onClick={(e) => e.stopPropagation()}>
            <Link target="_blank" url={LINK.ORDER_DETAIL(shop, String(shipment.orderId))}>
              Add tracking
            </Link>
          </div>
        </IndexTable.Cell>
        <IndexTable.Cell>{shipment.lineItems.length}</IndexTable.Cell>
        <IndexTable.Cell>{convertCurrency(shipment.subtotalPrice, shipment.currency || '')}</IndexTable.Cell>
        <IndexTable.Cell>{shippingAddress || '---'}</IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  const headings = useMemo((): NonEmptyArray<IndexTableHeading> => {
    if (shipmentFilter.type === EShipmentType.TRACKED_SHIPMENT) {
      return [
        { title: 'Tracking number' },
        { title: 'Order number' },
        { title: 'Delivery status' },
        { title: 'Carrier' },
        { title: 'Latest event' },
        { title: 'Sync status' },
        { title: 'Date' },
        { title: 'Processing time' },
        { title: 'Dispatch lead time' },
        { title: 'Delivery time' },
      ];
    }
    if (shipmentFilter.type === EShipmentType.UNFULFILLED_SHIPMENT) {
      return [
        { title: 'Order number' },
        { title: 'Order date' },
        { title: 'Waiting time' },
        { title: 'Fulfillment status' },
        { title: 'Items' },
        { title: 'Subtotal price' },
        { title: 'Shipping address' },
      ];
    }
    return [
      { title: 'Order number' },
      { title: 'Fulfillment date' },
      { title: 'Days untracked' },
      { title: 'Tracking' },
      { title: 'Items' },
      { title: 'Subtotal price' },
      { title: 'Shipping address' },
    ];
  }, [shipmentFilter.type]);

  return (
    <Card>
      <CustomTable
        filter={<ShipmentFilter data={data} isLoading={isLoading} type={shipmentFilter.type} courierRange={courierRange} />}
        tableProps={{
          resourceName: {
            singular: 'shipment',
            plural: 'shipments',
          },
          itemCount:
            shipmentFilter.status === ShipmentStatus.OverQuota ||
            (shipmentFilter.statusIn.length === 1 && shipmentFilter.statusIn[0] === ShipmentStatus.OverQuota) ||
            selectableShipments.length === 0
              ? shipmentList.length
              : selectableShipments.length,
          headings,
          emptyState: shipmentFilter.search?.includes('@') ? (
            <EmptySearchResult
              title="No shipments found"
              description="No shipment matches this email in the selected date range. Try widening the date range."
              withIllustration
            />
          ) : undefined,
          promotedBulkActions:
            shipmentFilter.type === EShipmentType.TRACKED_SHIPMENT
              ? [
                  {
                    content: 'Edit Courier',
                    onAction: handleSelectCourierByCheckbox,
                  },
                ]
              : undefined,
          selectedItemsCount: allResourcesSelected ? 'All' : selectedResources.length,
          onSelectionChange: handleSelectionChange,
          selectable:
            shipmentFilter.status === ShipmentStatus.OverQuota ||
            (shipmentFilter.statusIn.length === 1 && shipmentFilter.statusIn[0] === ShipmentStatus.OverQuota) ||
            selectableShipments.length === 0
              ? false
              : true,
        }}
        skeletonLines={13}
        isLoading={isFetching}
        page={shipmentFilter.page}
        pageSize={shipmentFilter.pageSize}
        totalResult={data?.data.totalResult}
        paginationProps={{ onPrevious: () => handleClickPaginationBtn('prev'), onNext: () => handleClickPaginationBtn('next') }}
      >
        {rowMarkup}
      </CustomTable>

      <CourierModal clearSelection={clearSelection} />

      <StatusModal />
    </Card>
  );
};

export default ShipmentTable;
